<template>
  <div class="be-room-card__col be-room-card__col--price">
    <template v-if="isAvailable">
      <div v-if="isPromotionShown || isMemberPriceShown" class="be-room-card__pills">
        <span v-if="isPromotionShown" class="be-room-card__pill-direct">{{ promotionLabel }}</span>
        <span v-if="isMemberPriceShown" class="be-room-card__pill-direct">Exclusive Member Price</span>
      </div>
      <div v-if="hasDiscount" class="be-room-card__discount">
        <span class="be-room-card__pill-off">{{ discountPercent }}% OFF</span>
        <span class="be-room-card__price-strike">{{ formatCurrency(basePrice) }}</span>
      </div>
      <p class="be-room-card__price-final">{{ formatCurrency(finalPrice) }}</p>
      <p class="be-room-card__price-note">{{ totalNightText }}</p>
      <p v-if="isShowTax" class="be-room-card__price-tax">Taxes &amp; Fees included</p>
      <template v-if="$showRoomPriceDecoy(rate.dates)">
        <RoomPriceDecoy :room-rate="rate" use-tooltip />
      </template>
    </template>
    <template v-else-if="rate.bookable">
      <div class="be-room-card__soldout">{{ $t('hotel.sold-out') }}</div>
    </template>
    <template v-else>
      <div :id="`v3-na-${rate.id}`" class="be-room-card__notavailable">
        NOT AVAILABLE<i class="simple-icon-question ml-2"></i>
      </div>
      <b-tooltip :target="`v3-na-${rate.id}`" triggers="hover" placement="bottom">
        <div class="p-2" v-html="notAvailableMessage"></div>
      </b-tooltip>
    </template>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { formatCurrency } from '@/utils/index.js';
  import RoomPriceDecoy from '../../RoomPriceDecoy.vue';
  import { getTotalPriceFromDate, discountAccumulation, checkDiscount, getAppliedPromotionLabel } from './pricing.js';

  export default {
    name: 'RoomCardRatePrice',
    components: {
      RoomPriceDecoy
    },
    props: {
      rate: {
        type: Object,
        default: () => ({})
      },
      type: {
        type: Object,
        default: () => ({})
      },
      avail: {
        type: Number,
        default: 0
      }
    },
    computed: {
      isAvailable(){
        return this.rate.bookable && this.avail > 0;
      },
      isShowTax(){
        return this.getMerchant().search_room_is_show_tax_fee_note;
      },
      hasDiscount(){
        return checkDiscount(this.rate.dates);
      },
      promotionLabel(){
        return getAppliedPromotionLabel(this.rate.dates);
      },
      isPromotionShown(){
        return this.hasDiscount && this.promotionLabel !== '';
      },
      isMemberPriceShown(){
        return this.$showDiscountMembership(this.rate.dates);
      },
      basePrice(){
        const dates = this.rate.dates;
        return getTotalPriceFromDate(dates, 'price', this.isShowTax) + getTotalPriceFromDate(dates, 'extra_guest_price', this.isShowTax);
      },
      finalPrice(){
        return getTotalPriceFromDate(this.rate.dates, 'final_price', this.isShowTax, true);
      },
      discountPercent(){
        return discountAccumulation(this.finalPrice, this.basePrice);
      },
      totalNightText(){
        const totalNight = this.rate.dates.length
        const unit = this.$t(`hotel.unit-type.${this.type.type}.per-unit`);
        return `${unit} for ${totalNight} ${this.$pluralWord(totalNight, 'night')}`
      },
      notAvailableMessage(){
        const roomRate = this.rate;
        if(!roomRate) return '';

        if(roomRate.stop_sell) return 'Sorry, the room you selected is unavailable for booking on the chosen dates.';

        const { check_in, check_out } = this.getBeFilter().date

        if(roomRate.closed_to_arrival){
          const checkinDate = this.$formatDate(check_in);
          return `Sorry, we cannot accept new arrivals for check-in on <b>${checkinDate}</b>. Please choose a different check-in date.`;
        }

        if(roomRate.closed_to_departure){
          const checkoutDate = this.$formatDate(check_out);
          return `Sorry, check-out on <b>${checkoutDate}</b> is not available. Please adjust your stay by extending or shortening your booking.`;
        }

        const totalNightStay = this.$countDays(check_in, check_out);

        if(totalNightStay < roomRate.min_nights) return `Sorry, the room requires a minimum stay of <b>${roomRate.min_nights} ${this.$pluralWord(roomRate.min_nights, 'night')}</b>. Please extend your stay to meet the requirement.`;

        if(totalNightStay > roomRate.max_nights) return `Sorry, the maximum stay for this room is <b>${roomRate.max_nights} ${this.$pluralWord(roomRate.max_nights, 'night')}</b>. Please shorten your booking to fit within the allowed stay.`;

        return '';
      }
    },
    methods: {
      ...mapGetters(['getMerchant', 'getBeFilter']),
      formatCurrency(value){
        return formatCurrency(value);
      }
    }
  }
</script>

<style scoped>
/* right-aligned at every width — on mobile the price block sits on its own row
   above the guest/qty pair and reads as the row's total */
.be-room-card__col--price {
  text-align: right;
}
.be-room-card__pills {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
  margin-bottom: 6px;
}
.be-room-card__pill-direct {
  display: inline-block;
  max-width: 100%;
  overflow-wrap: anywhere;
  background: #fdecec;
  color: #d64545;
  font-size: var(--be-v3-text-caption, 0.75rem);
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}
.be-room-card__discount {
  margin-bottom: 4px;
}
.be-room-card__pill-off {
  background: #e53935;
  color: #fff;
  font-size: var(--be-v3-text-caption, 0.75rem);
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 6px;
}
.be-room-card__price-strike {
  color: #767676;
  text-decoration: line-through;
  font-size: var(--be-v3-text-caption, 0.75rem);
  font-variant-numeric: tabular-nums;
}
.be-room-card__price-final {
  font-weight: 800;
  /* BeV3Sans caps at 800 and merchant fonts collapse all weights to one file,
     so stroke is what actually thickens the price */
  -webkit-text-stroke: 0.4px currentColor;
  font-size: var(--be-v3-text-price, 1.25rem);
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: #dc3545;
  margin: 2px 0;
}
.be-room-card__price-note {
  font-size: var(--be-v3-text-caption, 0.75rem);
  color: #6d6d6d;
  margin: 0;
}
.be-room-card__price-tax {
  font-size: var(--be-v3-text-caption, 0.75rem);
  font-weight: 400;
  color: #1a1a2e;
  margin: 0;
}
.be-room-card__soldout,
.be-room-card__notavailable {
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 600;
  font-size: var(--be-v3-text-secondary, 0.8125rem);
}
.be-room-card__soldout {
  background: #f1f1f1;
  color: #6d6d6d;
}
.be-room-card__notavailable {
  background: #fdecec;
  color: #c62828;
  border: 1px solid #f5c6c6;
  letter-spacing: 0.05em;
}
@media screen and (min-width: 992px) {
  .be-room-card__soldout,
  .be-room-card__notavailable {
    justify-content: center;
  }
}
/* Jost (the v3 default content font) is a real variable font that already gets
   heavier at font-weight 800 — the stroke above was tuned for BeV3Sans/merchant
   single-file fonts and doubled up on Jost. Keep a lighter stroke here (rather
   than dropping it entirely) since the price still wants a touch more emphasis
   than body text. */
.be-v3-content-font-default .be-room-card__price-final {
  -webkit-text-stroke: 0.15px currentColor;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/room-card/RoomCardRatePrice.vue